import { useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useApi } from "@lending/lib/api";
import { getMembershipShareEndpoint } from "@lending/pages/Common/MembershipShare/hooks/getMembershipShareEndpoint";
import { useStore } from "../../../../../contexts/StoreContext";
import type { MembershipAccountApplicationAuthenticationRequiredResponse } from "@lending/types";

type CreateMembershipAccountApplicationResult =
  | {
      status: "success";
      accountApplicationUuid: string | null;
    }
  | {
      status: "authentication_required";
    }
  | {
      status: "email_conflict";
    }
  | {
      status: "error";
    };

function isAuthenticationRequiredError(
  error: unknown
): error is MembershipAccountApplicationAuthenticationRequiredResponse {
  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as Partial<MembershipAccountApplicationAuthenticationRequiredResponse>;
  return candidate.id === "ao_authentication_required";
}

function isEmailConflictError(error: unknown): error is { id: string } {
  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as Partial<{ id: string }>;
  return candidate.id === "membership_share_email_conflict";
}

function useCreateMembershipAccountApplication() {
  const { l10n } = useLocalization();
  const $api = useApi();
  const queryClient = useQueryClient();
  const { updateData } = useStore();
  const { showErrorToast } = useToastsContext();

  const lendingApplicationUUID = useLendingApplicationID();
  const applicationType = useProductType();
  const endpoint = getMembershipShareEndpoint(applicationType, "membership_account_application");
  const membershipAccountCreateError = l10n.getString(
    "error-membership-account-create",
    null,
    "There was an error creating your membership account. Please try again."
  );

  const applicationQueryKeyByProductType = {
    "personal-loan": ["get", "/v2/lending/personal_loan_applications/{uuid}/"],
    "credit-card": ["get", "/v2/lending/credit_card_applications/{uuid}/"],
    "auto-loan": ["get", "/v2/lending/vehicle_loan_applications/{uuid}/"],
  } as const;

  const mutation = $api.useMutation("post", endpoint, {
    onSuccess: async (response) => {
      if (response.id) {
        updateData({ accountApplicationUuid: response.id });
      }

      if (applicationType) {
        // Refresh the application so account_application_uuid is populated before the
        // route policy re-evaluates; otherwise the disclosures route bounces back to activate.
        await queryClient.invalidateQueries({
          queryKey: applicationQueryKeyByProductType[applicationType],
        });
      }
    },
    onError: (error) => {
      if (isAuthenticationRequiredError(error)) {
        return;
      }

      if (isEmailConflictError(error)) {
        return;
      }

      Sentry.captureException(error, {
        extra: { lendingApplicationUUID, applicationType },
      });
      showErrorToast(membershipAccountCreateError);
    },
  });

  const createMembershipAccountApplication =
    async (): Promise<CreateMembershipAccountApplicationResult> => {
      if (!lendingApplicationUUID) {
        throw new Error("Membership account application endpoint is not available");
      }

      try {
        const response = await mutation.mutateAsync({
          params: { path: { uuid: lendingApplicationUUID } },
          body: {} as never,
        });

        if (!response.id) {
          showErrorToast(membershipAccountCreateError);
          return { status: "error" };
        }

        return {
          status: "success",
          accountApplicationUuid: response.id,
        };
      } catch (error) {
        if (isAuthenticationRequiredError(error)) {
          return { status: "authentication_required" };
        }

        if (isEmailConflictError(error)) {
          return { status: "email_conflict" };
        }

        return { status: "error" };
      }
    };

  return {
    createMembershipAccountApplication,
    isPending: mutation.isPending,
  };
}

export { useCreateMembershipAccountApplication };
